/**
 * <CountryFilterDropdown /> — country picker for the Admin Dashboard.
 *
 * Renders a compact button in the dashboard header showing the
 * currently-selected country (flag + label) or "Tutti i paesi" when no
 * filter is active. Click opens a dropdown with all 18 supported
 * countries.
 *
 * The list mirrors the backend ``OPERATION_CENTERS`` mapping in
 * ``services/i18n.py``. We hardcode the labels here (instead of
 * fetching ``/api/operation-centers`` on mount) because:
 *   1. Saves a network round-trip on every dashboard load.
 *   2. The list is effectively static — new countries are an annual
 *      event, not a daily one.
 *   3. The translation strings are ours, not user-generated.
 * If a country is ever added/removed on the backend, mirror it here.
 */

import { useEffect, useRef, useState } from "react";
import { Check, ChevronDown, Globe2 } from "lucide-react";
import { useCountryFilter } from "../../contexts/CountryFilterContext";
import { CountryFlag } from "./CountryFlag";

// Display order is roughly EU west-to-east + UK extras at the end, to
// match the natural reading flow of an Italian-speaking ops team.
const COUNTRIES = [
  { code: "IT",  label: "Italia" },
  { code: "FR",  label: "France" },
  { code: "ES",  label: "España" },
  { code: "DE",  label: "Deutschland" },
  { code: "NL",  label: "Nederland" },
  { code: "BE",  label: "Belgique / België" },
  { code: "PT",  label: "Portugal" },
  { code: "CH",  label: "Schweiz" },
  { code: "PL",  label: "Polska" },
  { code: "CZ",  label: "Česká republika" },
  { code: "SE",  label: "Sverige" },
  { code: "NO",  label: "Norge" },
  { code: "FI",  label: "Suomi" },
  { code: "DK",  label: "Danmark" },
  { code: "GR",  label: "Elláda" },
  { code: "UK",  label: "United Kingdom" },
  { code: "IRL", label: "Ireland" },
  { code: "GBZ", label: "Gibraltar" },
];

export const CountryFilterDropdown = () => {
  const { country, setCountry, isFiltered, ALL } = useCountryFilter();
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);

  // Close on outside click / Escape — standard dropdown plumbing.
  useEffect(() => {
    if (!open) return undefined;
    const onClick = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const currentLabel = isFiltered
    ? (COUNTRIES.find((c) => c.code === country)?.label || country)
    : "Tutti i paesi";

  return (
    <div className="relative" ref={rootRef}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-sm transition
          ${isFiltered
            ? "border-lojack-blue-500/60 bg-lojack-blue-500/10 text-lojack-blue-300 hover:bg-lojack-blue-500/20"
            : "border-gray-700 bg-gray-900/40 text-gray-300 hover:bg-gray-800/60"}`}
        title={isFiltered ? `Filtrato per ${currentLabel}` : "Filtra dashboard per paese"}
        data-testid="country-filter-toggle"
      >
        {isFiltered ? (
          <CountryFlag code={country} className="!w-[18px] !h-[13px]" />
        ) : (
          <Globe2 size={14} className="text-gray-400" />
        )}
        <span className="truncate max-w-[150px]">{currentLabel}</span>
        <ChevronDown size={14} className={`transition ${open ? "rotate-180" : ""}`} />
      </button>

      {open && (
        <div
          className="absolute right-0 mt-2 w-64 max-h-[420px] overflow-y-auto rounded-xl border border-gray-700 bg-[#0f0f0f] shadow-2xl z-50"
          data-testid="country-filter-menu"
        >
          {/* "All" reset option — pinned at top */}
          <button
            type="button"
            onClick={() => { setCountry(ALL); setOpen(false); }}
            className={`w-full flex items-center gap-3 px-3 py-2.5 text-sm text-left hover:bg-gray-800/60 transition
              ${!isFiltered ? "text-lojack-blue-300" : "text-gray-300"}`}
            data-testid="country-filter-option-ALL"
          >
            <Globe2 size={16} className="flex-shrink-0" />
            <span className="flex-1">Tutti i paesi</span>
            {!isFiltered && <Check size={14} className="text-lojack-blue-400" />}
          </button>

          <div className="border-t border-gray-800" />

          {COUNTRIES.map(({ code, label }) => {
            const selected = country === code;
            return (
              <button
                key={code}
                type="button"
                onClick={() => { setCountry(code); setOpen(false); }}
                className={`w-full flex items-center gap-3 px-3 py-2 text-sm text-left hover:bg-gray-800/60 transition
                  ${selected ? "bg-lojack-blue-500/10 text-lojack-blue-200" : "text-gray-300"}`}
                data-testid={`country-filter-option-${code}`}
              >
                <CountryFlag code={code} className="flex-shrink-0" />
                <span className="flex-1">{label}</span>
                <span className="text-gray-600 text-xs">{code}</span>
                {selected && <Check size={14} className="text-lojack-blue-400" />}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default CountryFilterDropdown;
